import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'h2', 'h3', 'ul', 'ol', 'li'];
const ALLOWED_ATTR = [];

const SANITIZE_OPTIONS = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
};

export function isLikelyHtml(value) {
  return typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value);
}

export function escapeHtmlText(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Turns common escaped HTML entities back into markup when the payload looks escaped.
 * @param {string} value
 * @returns {string}
 */
export function unescapeCourseDescriptionEntities(value) {
  if (typeof value !== 'string' || value.trim() === '') {
    return '';
  }
  const trimmed = value.trim();
  if (!/&lt;\/?[a-z]/i.test(trimmed)) {
    return trimmed;
  }
  return trimmed
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, '&');
}

/**
 * Converts a small subset of markdown used by the course editor into HTML.
 * @param {string} value
 * @returns {string}
 */
export function markdownLiteToCourseDescriptionHtml(value) {
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return '';
  }

  const lines = trimmed.split(/\r?\n/);
  /** @type {string[]} */
  const htmlParts = [];
  /** @type {string[]} */
  let listItems = [];
  let listType = null;

  const flushList = () => {
    if (!listType || listItems.length === 0) {
      listItems = [];
      listType = null;
      return;
    }
    const tag = listType;
    htmlParts.push(`<${tag}>${listItems.map((item) => `<li>${item}</li>`).join('')}</${tag}>`);
    listItems = [];
    listType = null;
  };

  const formatInline = (text) => {
    const escaped = escapeHtmlText(text);
    return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line === '') {
      flushList();
      continue;
    }

    const unordered = line.match(/^[-*]\s+(.+)$/);
    if (unordered) {
      if (listType && listType !== 'ul') {
        flushList();
      }
      listType = 'ul';
      listItems.push(formatInline(unordered[1]));
      continue;
    }

    const ordered = line.match(/^\d+\.\s+(.+)$/);
    if (ordered) {
      if (listType && listType !== 'ol') {
        flushList();
      }
      listType = 'ol';
      listItems.push(formatInline(ordered[1]));
      continue;
    }

    flushList();

    if (line.startsWith('### ')) {
      htmlParts.push(`<h3>${formatInline(line.slice(4))}</h3>`);
      continue;
    }
    if (line.startsWith('## ')) {
      htmlParts.push(`<h2>${formatInline(line.slice(3))}</h2>`);
      continue;
    }

    htmlParts.push(`<p>${formatInline(line)}</p>`);
  }

  flushList();
  return htmlParts.join('');
}

export function sanitizeCourseDescriptionHtml(html) {
  if (typeof html !== 'string' || html.trim() === '') {
    return '';
  }
  return DOMPurify.sanitize(html, SANITIZE_OPTIONS).trim();
}

export function plainTextToCourseDescriptionHtml(text) {
  if (typeof text !== 'string') {
    return '';
  }
  const trimmed = text.trim();
  if (trimmed === '') {
    return '';
  }

  const unescaped = unescapeCourseDescriptionEntities(trimmed);
  if (isLikelyHtml(unescaped)) {
    return sanitizeCourseDescriptionHtml(unescaped);
  }

  if (
    /(\*\*.+\*\*|^\s{0,3}#{2,3}\s+|^\s*[-*]\s+|\d+\.\s+)/m.test(unescaped)
  ) {
    return sanitizeCourseDescriptionHtml(markdownLiteToCourseDescriptionHtml(unescaped));
  }

  return unescaped
    .split(/\n{2,}/)
    .map((paragraph) => {
      const lines = paragraph.split('\n').map((line) => escapeHtmlText(line.trim())).filter(Boolean);
      if (lines.length === 0) {
        return '';
      }
      if (lines.length === 1) {
        return `<p>${lines[0]}</p>`;
      }
      return `<p>${lines.join('<br>')}</p>`;
    })
    .filter(Boolean)
    .join('');
}

export function prepareCourseDescriptionForEditor(value) {
  return plainTextToCourseDescriptionHtml(value ?? '');
}

export function getCourseDescriptionPlainText(html) {
  if (typeof html !== 'string' || html.trim() === '') {
    return '';
  }
  const sanitized = sanitizeCourseDescriptionHtml(
    isLikelyHtml(html) ? html : plainTextToCourseDescriptionHtml(html),
  );
  if (sanitized === '') {
    return '';
  }
  if (typeof document !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = sanitized;
    return (container.textContent ?? '').replace(/\s+/g, ' ').trim();
  }
  return sanitized
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|h2|h3|li)>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function getCourseDescriptionPlainTextLength(html) {
  return getCourseDescriptionPlainText(html).length;
}

export function isCourseDescriptionEmpty(html) {
  return getCourseDescriptionPlainTextLength(html) === 0;
}
